import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { login } from 'actions/loginActions';

import Container from 'components/auth/Container';
import Link from 'components/routes/Link';

import { useTitle } from 'hooks';

interface FormBody {
  email: string;
  password: string;
}

const LoginPage = () => {
  useTitle('Login');

  const { formState, handleSubmit, register } = useForm<FormBody>();

  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const handleRedirect = useCallback(() => {
    // determine place to redirect if accessed from different page
    const state = location?.state as
      | { from: string; search: string }
      | undefined;

    const search = state?.search || '';
    const redirect = state?.from ?? '/';

    navigate(redirect + search, { replace: true });
  }, [location, navigate]);

  const handleLogin = async (body: FormBody) => {
    try {
      await login(body!);
      handleRedirect();
    } catch (err) {
      if (err?.code === 1007) {
        // if code is 1007, that means the user should login using sso
        navigate({
          pathname: '/login/sso',
          search: new URLSearchParams({
            email: body.email,
          }).toString(),
        });
      } else {
        enqueueSnackbar(err.message, {
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(handleLogin)}>
      <Container>
        <Typography component="h1" marginBottom={2} variant="h2">
          Welcome to CourseKey!
        </Typography>

        <Stack marginBottom={2}>
          <TextField
            fullWidth
            id="login-email-input"
            inputProps={{ ...register('email') }}
            label="Email"
            margin="normal"
            required
            size="large"
            type="email"
          />
          <TextField
            fullWidth
            id="login-password-input"
            inputProps={{ ...register('password') }}
            label="Password"
            margin="normal"
            required
            size="large"
            type="password"
          />
        </Stack>
        <Box>
          <Button
            disabled={formState.isSubmitting}
            fullWidth
            type="submit"
            variant="contained"
          >
            Sign In
          </Button>
          <Box alignItems="center" display="flex" gap={4} my={2}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography>or</Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>
          <Button component={Link} fullWidth to="/login/sso" variant="outlined">
            Sign in with SSO
          </Button>
        </Box>

        <Box marginTop={4}>
          <Typography
            align="center"
            color="textSecondary"
            data-testid="login-links"
            variant="subtitle1"
          >
            Don't have an account? <Link to="/register">Sign up!</Link>
            <br />
            Forgot <Link to="/forgot-email">email</Link> or{' '}
            <Link to="/forgot-password">password?</Link>
          </Typography>
        </Box>
      </Container>
    </form>
  );
};

export default LoginPage;
